import { useCallback, useMemo, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { Alert, Chip, CircularProgress, Divider } from "@mui/material";
import * as XLSX from "xlsx";
import "./add-candidates-bulk.modal.scss";
import { Candidate } from "../model/candidate.model";

type ParsedRow = {
  idx: number;
  candidate: Candidate;
  emailNorm: string;
  isInvalid: boolean;
  invalidReason?: string;
  isDuplicateInFile: boolean;
  isAlreadyInvited: boolean;
};

interface AddCandidatesBulkModalProps {
  isOpen: boolean;
  onClose(): void;
  existingEmails?: string[]; // emails already invited in this job_question
  onConfirmInvite(candidates: Candidate[], meta: { total: number; skippedAlreadyInvited: number; skippedInvalid: number }): Promise<void> | void;
}

function normalizeEmail(v: unknown) {
  return String(v || "").trim().toLowerCase();
}

function normHeader(v: unknown) {
  return String(v || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/[()]/g, "")
    .replace(/[_-]/g, "");
}

function excelDateToISO(v: unknown): string | null {
  if (v === null || v === undefined || v === "") return null;
  if (v instanceof Date && Number.isFinite(v.getTime())) return v.toISOString();
  if (typeof v === "number" && Number.isFinite(v)) {
    const d = XLSX.SSF.parse_date_code(v);
    if (!d) return null;
    const dt = new Date(Date.UTC(d.y, (d.m || 1) - 1, d.d || 1, d.H || 0, d.M || 0, d.S || 0));
    return Number.isFinite(dt.getTime()) ? dt.toISOString() : null;
  }
  const s = String(v).trim();
  if (!s) return null;
  const d = new Date(s);
  return Number.isFinite(d.getTime()) ? d.toISOString() : null;
}

function getCell(row: Record<string, unknown>, keys: string[]) {
  for (const k of keys) {
    const hit = Object.keys(row).find((rk) => normHeader(rk) === normHeader(k));
    if (hit) return row[hit];
  }
  return undefined;
}

/** เบอร์โทรเก็บเป็นข้อความตามที่กรอก — ไม่บังคับรูปแบบ (0957915540, 095-791-5540, +66 …) */
function phoneCellToString(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v.trim();
  if (typeof v === "number" && Number.isFinite(v)) return String(v);
  if (typeof v === "bigint") return String(v);
  return String(v).trim();
}

function mapRowToCandidate(row: Record<string, unknown>): Candidate {
  const firstname = String(getCell(row, ["firstname", "ชื่อ", "first_name", "first name"]) || "").trim();
  const lastname = String(getCell(row, ["lastname", "นามสกุล", "last_name", "last name"]) || "").trim();
  const email = String(getCell(row, ["email", "อีเมล", "e-mail"]) || "").trim();
  const phone = phoneCellToString(
    getCell(row, [
      "phone",
      "phone number",
      "phonenumber",
      "mobile",
      "mobile number",
      "tel",
      "telephone",
      "เบอร์โทรศัพท์",
      "เบอร์โทร",
      "โทรศัพท์",
    ]),
  );
  const jobTitle = String(getCell(row, ["jobtitle", "ตำแหน่งงานปัจจุบัน", "job_title", "ตำแหน่ง"]) || "").trim();
  const jobExpRaw = getCell(row, ["jobexp", "ประสบการณ์ทั้งหมด", "job_exp", "experience"]);
  const jobExp = Number.isFinite(Number(jobExpRaw)) ? Number(jobExpRaw) : 0;

  const startRaw = getCell(row, ["interview_start_date", "start_date", "วันที่เริ่มสัมภาษณ์", "วันที่เริ่ม"]);
  const endRaw = getCell(row, ["interview_end_date", "end_date", "วันสิ้นสุดการสัมภาษณ์", "วันที่สิ้นสุด"]);
  const nowIso = new Date().toISOString();
  const interview_start_date = excelDateToISO(startRaw) || nowIso;
  const interview_end_date = excelDateToISO(endRaw) || nowIso;

  return {
    firstname,
    lastname,
    email,
    phone,
    jobTitle,
    jobExp,
    status: "ACTIVE",
    interview_start_date,
    interview_end_date,
    resume: null,
    candidate_id: "",
  };
}

async function readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
  return await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(new Error("Failed to read file"));
    r.onload = () => resolve(r.result as ArrayBuffer);
    r.readAsArrayBuffer(file);
  });
}

async function readFileAsText(file: File): Promise<string> {
  return await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(new Error("Failed to read file"));
    r.onload = () => resolve(String(r.result || ""));
    r.readAsText(file);
  });
}

async function parseCandidatesFile(file: File): Promise<Record<string, unknown>[]> {
  const name = file.name.toLowerCase();
  if (name.endsWith(".csv")) {
    const text = await readFileAsText(file);
    const wb = XLSX.read(text, { type: "string" });
    const ws = wb.Sheets[wb.SheetNames[0]];
    return XLSX.utils.sheet_to_json(ws, { defval: "" }) as Record<string, unknown>[];
  }
  const buf = await readFileAsArrayBuffer(file);
  const wb = XLSX.read(buf, { type: "array" });
  const ws = wb.Sheets[wb.SheetNames[0]];
  return XLSX.utils.sheet_to_json(ws, { defval: "" }) as Record<string, unknown>[];
}

const AddCandidatesBulkModal: React.FC<AddCandidatesBulkModalProps> = ({
  isOpen,
  onClose,
  existingEmails,
  onConfirmInvite,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [isParsing, setIsParsing] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const existingEmailSet = useMemo(() => {
    const s = new Set<string>();
    (existingEmails || []).forEach((e) => {
      const ne = normalizeEmail(e);
      if (ne) s.add(ne);
    });
    return s;
  }, [existingEmails]);

  const reset = useCallback(() => {
    setFile(null);
    setIsParsing(false);
    setParseError(null);
    setRows([]);
    setIsSubmitting(false);
  }, []);

  const close = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const parseFile = useCallback(
    async (f: File) => {
      setFile(f);
      setIsParsing(true);
      setParseError(null);
      setRows([]);
      try {
        const raw = await parseCandidatesFile(f);
        const mapped: ParsedRow[] = raw.map((r, i) => {
          const candidate = mapRowToCandidate(r);
          const emailNorm = normalizeEmail(candidate.email);
          const isInvalid = !emailNorm;
          const invalidReason = !emailNorm ? "Missing email" : undefined;
          return {
            idx: i + 1,
            candidate,
            emailNorm,
            isInvalid,
            invalidReason,
            isDuplicateInFile: false,
            isAlreadyInvited: emailNorm ? existingEmailSet.has(emailNorm) : false,
          };
        });

        const seen = new Set<string>();
        const withDup = mapped.map((m) => {
          if (!m.emailNorm) return m;
          const dup = seen.has(m.emailNorm);
          seen.add(m.emailNorm);
          return { ...m, isDuplicateInFile: dup };
        });

        setRows(withDup);
      } catch (e: any) {
        setParseError(e?.message || "Failed to parse file");
      } finally {
        setIsParsing(false);
      }
    },
    [existingEmailSet],
  );

  const onDrop = useCallback(
    (ev: React.DragEvent) => {
      ev.preventDefault();
      const f = ev.dataTransfer.files?.[0];
      if (f) parseFile(f);
    },
    [parseFile],
  );

  const totals = useMemo(() => {
    const total = rows.length;
    const invalid = rows.filter((r) => r.isInvalid).length;
    const dupInFile = rows.filter((r) => r.isDuplicateInFile).length;
    const already = rows.filter((r) => r.isAlreadyInvited).length;
    const canInvite = rows.filter((r) => !r.isInvalid && !r.isDuplicateInFile && !r.isAlreadyInvited).length;
    return { total, invalid, dupInFile, already, canInvite };
  }, [rows]);

  const confirmInvite = useCallback(async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const inviteList = rows
        .filter((r) => !r.isInvalid && !r.isDuplicateInFile && !r.isAlreadyInvited)
        .map((r) => r.candidate);
      const meta = {
        total: rows.length,
        skippedAlreadyInvited: totals.already,
        skippedInvalid: totals.invalid + totals.dupInFile,
      };
      await onConfirmInvite(inviteList, meta);
      close();
    } finally {
      setIsSubmitting(false);
    }
  }, [close, isSubmitting, onConfirmInvite, rows, totals.already, totals.dupInFile, totals.invalid]);

  return (
    <Modal centered show={isOpen} onHide={close} dialogClassName="add-candidates-bulk-modal">
      <Modal.Body>
        <div className="acb-wrap">
          <div className="acb-header">
            <div>
              <div className="acb-title">เพิ่มผู้สมัครหลายคน</div>
              <div className="acb-subtitle">อัปโหลดไฟล์ .xlsx หรือ .csv เพื่อส่งคำเชิญผู้สมัครหลายคนในครั้งเดียว</div>
            </div>
            <Button className="acb-close" variant="light" onClick={close}>
              ปิด
            </Button>
          </div>

          <Divider />

          <div
            className="acb-dropzone"
            onDrop={onDrop}
            onDragOver={(e) => e.preventDefault()}
          >
            <input
              type="file"
              accept=".xlsx,.csv"
              className="acb-file"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) parseFile(f);
              }}
            />
            <div className="acb-dropzone-inner">
              <div className="acb-drop-title">ลากไฟล์มาวาง หรือเลือกไฟล์</div>
              <div className="acb-drop-hint">รองรับ .xlsx และ .csv</div>
              <Button className="button-std acb-browse" type="button">
                Browse file
              </Button>
              {file ? <div className="acb-file-name">{file.name}</div> : null}
            </div>
          </div>

          {parseError ? (
            <Alert severity="error" sx={{ marginTop: "12px" }}>
              {parseError}
            </Alert>
          ) : null}

          {isParsing ? (
            <div className="acb-loading">
              <CircularProgress size={22} />
              <span>กำลังอ่านไฟล์...</span>
            </div>
          ) : null}

          {rows.length > 0 ? (
            <>
              <div className="acb-summary">
                <Chip label={`ทั้งหมด: ${totals.total}`} />
                <Chip color="success" label={`พร้อมเชิญ: ${totals.canInvite}`} />
                <Chip color="warning" label={`ซ้ำในไฟล์: ${totals.dupInFile}`} />
                <Chip color="warning" label={`เชิญแล้ว: ${totals.already}`} />
                <Chip color="error" label={`ไม่ถูกต้อง: ${totals.invalid}`} />
              </div>

              <div className="acb-table-wrap">
                <table className="acb-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>ชื่อ</th>
                      <th>อีเมล</th>
                      <th>สถานะ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, 300).map((r) => {
                      const name = `${r.candidate.firstname || ""} ${r.candidate.lastname || ""}`.trim() || "-";
                      let status = "พร้อมเชิญ";
                      let cls = "ok";
                      if (r.isInvalid) {
                        status = r.invalidReason || "ข้อมูลไม่ครบ";
                        cls = "bad";
                      } else if (r.isDuplicateInFile) {
                        status = "อีเมลซ้ำในไฟล์";
                        cls = "warn";
                      } else if (r.isAlreadyInvited) {
                        status = "ส่งคำเชิญแล้ว";
                        cls = "warn";
                      }
                      return (
                        <tr key={r.idx}>
                          <td className="muted">{r.idx}</td>
                          <td>{name}</td>
                          <td className="mono">{r.candidate.email || "-"}</td>
                          <td>
                            <span className={`acb-status acb-status--${cls}`}>{status}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {rows.length > 300 ? (
                  <div className="acb-note muted">แสดงตัวอย่าง 300 แถวแรก (รวมทั้งหมด {rows.length} แถว)</div>
                ) : null}
              </div>

              <div className="acb-actions">
                <Button variant="light" onClick={close} disabled={isSubmitting}>
                  ยกเลิก
                </Button>
                <Button
                  className="button-std"
                  onClick={confirmInvite}
                  disabled={isSubmitting || totals.canInvite === 0}
                >
                  {isSubmitting ? "กำลังส่ง..." : `ส่งคำเชิญทั้งหมด (${totals.canInvite})`}
                </Button>
              </div>
            </>
          ) : null}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default AddCandidatesBulkModal;

